import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { AvatarIcon } from './AvatarIcon'
import { ApiClientError } from '@/lib/api/client'
import {
  AVATAR_PRESETS,
  DEFAULT_AVATAR,
  parseAvatarConfig,
  randomAvatar,
  type AvatarConfig,
} from './avatar-catalog'
import { AVATAR_SECTIONS } from './avatar-editor-sections'
import { AvatarOptions } from './AvatarOptions'
import { DonorAvatar } from './DonorAvatar'
import styles from './AvatarEditor.module.css'
import type { AvatarPiece } from './avatar-custom'
import { equipAvatarPiece } from './avatar-custom'
import { AvatarPurchaseDialog } from './AvatarPurchaseDialog'

type Props = {
  initialAvatar?: unknown
  onSave: (config: AvatarConfig) => Promise<void>
  onClose: () => void
  campaignItems?: AvatarPiece[]
  canSave?: boolean
  catalogError?: string | null
  onRetryCatalog?: () => void
  campaignId?: number
  institutionName?: string
  salesOpen?: boolean
  onPurchased?: (itemId: number) => Promise<AvatarPiece>
}

export default function AvatarEditor({
  initialAvatar,
  onSave,
  onClose,
  campaignItems,
  canSave = true,
  catalogError,
  onRetryCatalog,
  campaignId,
  institutionName = 'a entidade desta campanha',
  salesOpen = false,
  onPurchased,
}: Props) {
  const [initial, setInitial] = useState(() => parseAvatarConfig(initialAvatar))
  const [draft, setDraft] = useState<AvatarConfig>(() => initial ?? { ...DEFAULT_AVATAR })
  const initialSource = useRef(JSON.stringify(parseAvatarConfig(initialAvatar)))
  const [history, setHistory] = useState<AvatarConfig[]>([])
  const [sectionIndex, setSectionIndex] = useState(0)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  const [purchasePiece, setPurchasePiece] = useState<AvatarPiece | null>(null)
  const savingRef = useRef(false)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const tabsRef = useRef<HTMLDivElement>(null)
  const mountedRef = useRef(false)
  const continueEditingRef = useRef<HTMLButtonElement>(null)
  const id = useId()
  const section = AVATAR_SECTIONS[sectionIndex]
  const changed = JSON.stringify(draft) !== JSON.stringify(initial ?? DEFAULT_AVATAR)

  useEffect(() => {
    const incoming = parseAvatarConfig(initialAvatar)
    const source = JSON.stringify(incoming)
    if (source === initialSource.current) return
    initialSource.current = source
    if (!incoming) return
    setDraft((current) => {
      const previous = initial ?? DEFAULT_AVATAR
      const merged = { ...incoming } as Record<string, unknown>
      for (const key of Object.keys(current) as (keyof AvatarConfig)[]) {
        if (JSON.stringify(current[key]) !== JSON.stringify(previous[key])) {
          merged[key] = current[key]
        }
      }
      return merged as AvatarConfig
    })
    setInitial(incoming)
  }, [initialAvatar, initial])

  useEffect(() => {
    mountedRef.current = true
    const dialog = dialogRef.current
    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    const previousOverflow = document.body.style.overflow
    dialog?.showModal()
    document.body.style.overflow = 'hidden'
    return () => {
      mountedRef.current = false
      dialog?.close()
      document.body.style.overflow = previousOverflow
      previousFocus?.focus({ preventScroll: true })
    }
  }, [])

  useEffect(() => {
    if (confirmDiscard) continueEditingRef.current?.focus()
  }, [confirmDiscard])

  const update = (next: AvatarConfig) => {
    if (savingRef.current) return
    setHistory((items) => [...items.slice(-19), draft])
    setDraft(next)
    setError(null)
    setConfirmDiscard(false)
  }

  const requestClose = () => {
    if (savingRef.current) return
    if (changed) setConfirmDiscard(true)
    else onClose()
  }

  const selectSection = (index: number) => {
    setSectionIndex(index)
    if (panelRef.current) panelRef.current.scrollTop = 0
  }

  const navigateTabs = (event: KeyboardEvent<HTMLDivElement>) => {
    let next: number
    if (event.key === 'ArrowRight') next = (sectionIndex + 1) % AVATAR_SECTIONS.length
    else if (event.key === 'ArrowLeft')
      next = (sectionIndex + AVATAR_SECTIONS.length - 1) % AVATAR_SECTIONS.length
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = AVATAR_SECTIONS.length - 1
    else return
    event.preventDefault()
    selectSection(next)
    const tab = tabsRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]
    tab?.focus()
    tab?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
  }

  const save = async () => {
    if (savingRef.current || !canSave) return
    savingRef.current = true
    setIsSaving(true)
    setError(null)
    setConfirmDiscard(false)
    try {
      await onSave(draft)
      if (mountedRef.current) onClose()
    } catch (err) {
      if (mountedRef.current)
        setError(
          err instanceof ApiClientError
            ? Object.values(err.errors ?? {}).flat()[0] || err.message
            : 'Não foi possível salvar seu avatar. Suas escolhas estão aqui; tente novamente.'
        )
    } finally {
      savingRef.current = false
      if (mountedRef.current) setIsSaving(false)
    }
  }

  return (
    <>
      {purchasePiece && campaignId && onPurchased && (
        <AvatarPurchaseDialog
          key={`${campaignId}:${purchasePiece.id}`}
          campaignId={campaignId}
          piece={purchasePiece}
          institutionName={institutionName}
          salesOpen={salesOpen}
          onPurchased={() => onPurchased(purchasePiece.id)}
          onClose={() => setPurchasePiece(null)}
          onUse={(piece) => {
            update(equipAvatarPiece(draft, piece))
            setPurchasePiece(null)
          }}
        />
      )}
      {createPortal(
        <dialog
          ref={dialogRef}
          className={styles.dialog}
          aria-labelledby={`${id}-title`}
          aria-describedby={`${id}-description`}
          onCancel={(event) => {
            event.preventDefault()
            requestClose()
          }}
        >
          <div className={styles.editor}>
            <header className={styles.header}>
              <span className={styles.brandMark} aria-hidden>
                <AvatarIcon name="face" />
              </span>
              <div>
                <p className={styles.eyebrow}>SEU ESPAÇO, SEU JEITO</p>
                <h2 id={`${id}-title`}>Meu avatar</h2>
              </div>
              <button
                type="button"
                className={styles.close}
                onClick={requestClose}
                disabled={isSaving}
                aria-label="Fechar editor de avatar"
              >
                <AvatarIcon name="close" />
              </button>
            </header>
            <div className={styles.workspace}>
              <aside
                className={styles.preview}
                style={{ '--avatar-background': draft.background } as React.CSSProperties}
              >
                <div className={styles.previewIntro}>
                  <span className={styles.liveDot} /> CRIADO POR VOCÊ
                </div>
                <div className={styles.characterStage}>
                  <span className={styles.sparkOne} aria-hidden>
                    ✦
                  </span>
                  <span className={styles.sparkTwo} aria-hidden>
                    ✧
                  </span>
                  <DonorAvatar
                    config={draft}
                    transparent
                    className={styles.character}
                    label="Prévia do seu avatar"
                  />
                  <span className={styles.ground} aria-hidden />
                </div>
                <div className={styles.previewCaption}>
                  <span className={styles.miniAvatar}>
                    <DonorAvatar config={draft} portrait />
                  </span>
                  <span>
                    <strong>Olha você aqui!</strong>
                    <small>Assim vai aparecer no seu perfil.</small>
                  </span>
                </div>
                <div className={styles.previewActions}>
                  <button
                    type="button"
                    onClick={() => update(randomAvatar())}
                    disabled={isSaving}
                  >
                    <AvatarIcon name="shuffle" /> Surpreenda-me
                  </button>
                  <button
                    type="button"
                    disabled={isSaving || !history.length}
                    aria-label="Desfazer última alteração"
                    onClick={() => {
                      const previous = history.at(-1)
                      if (previous) {
                        setDraft(previous)
                        setHistory((items) => items.slice(0, -1))
                        setConfirmDiscard(false)
                        setError(null)
                      }
                    }}
                  >
                    <AvatarIcon name="undo" />
                  </button>
                </div>
                <p className={styles.previewNote} id={`${id}-description`}>
                  Todos os estilos são seus.
                  <br />
                  Combine livremente e mude quando quiser.
                </p>
              </aside>
              <div className={styles.customize}>
                <div
                  className={styles.tabs}
                  role="tablist"
                  aria-label="Personalizar avatar"
                  ref={tabsRef}
                  onKeyDown={navigateTabs}
                >
                  {AVATAR_SECTIONS.map((item, index) => (
                    <button
                      key={item.id}
                      type="button"
                      role="tab"
                      id={`${id}-tab-${item.id}`}
                      aria-controls={`${id}-panel`}
                      aria-selected={index === sectionIndex}
                      tabIndex={index === sectionIndex ? 0 : -1}
                      onClick={() => selectSection(index)}
                    >
                      <AvatarIcon name={item.icon} />
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
                <div
                  className={styles.panel}
                  role="tabpanel"
                  id={`${id}-panel`}
                  aria-labelledby={`${id}-tab-${section.id}`}
                  ref={panelRef}
                  tabIndex={0}
                >
                  <div className={styles.sectionIntro}>
                    <h3>{section.title}</h3>
                    <p>{section.description}</p>
                  </div>
                  <fieldset disabled={isSaving} className={styles.controls}>
                    <legend className={styles.srOnly}>
                      Opções de {section.label.toLowerCase()}
                    </legend>
                    {section.id === 'base' && (
                      <fieldset className={styles.optionGroup}>
                        <legend>
                          Um ponto de partida<span>Depois, deixe com a sua cara</span>
                        </legend>
                        <div className={styles.presets}>
                          {AVATAR_PRESETS.map((preset) => (
                            <button
                              type="button"
                              key={preset.label}
                              onClick={() => update({ ...preset.config })}
                              aria-label={`Começar com ${preset.label}`}
                            >
                              <DonorAvatar config={preset.config} portrait />
                              <span>{preset.label}</span>
                            </button>
                          ))}
                        </div>
                      </fieldset>
                    )}
                    {section.id === 'hair' && draft.headwear !== 'none' && (
                      <p className={styles.hint}>
                        Seu acessório de cabeça pode cobrir parte do cabelo. Você pode
                        retirá-lo em Acessórios.
                      </p>
                    )}
                    {section.fields.map((field) => (
                      <AvatarOptions
                        key={field.field}
                        {...field}
                        config={draft}
                        onChange={update}
                        campaignItems={campaignItems}
                        onBuy={campaignId && onPurchased ? setPurchasePiece : undefined}
                      />
                    ))}
                  </fieldset>
                </div>
              </div>
            </div>
            <footer className={styles.footer}>
              {(error || catalogError) && (
                <p role="alert" className={styles.error}>
                  <AvatarIcon name="alert" />
                  {error || catalogError}
                  {catalogError && onRetryCatalog && (
                    <button type="button" onClick={onRetryCatalog}>
                      Tentar novamente
                    </button>
                  )}
                </p>
              )}
              {confirmDiscard ? (
                <div className={styles.discard}>
                  <p role="status">Sair sem salvar suas alterações?</p>
                  <button type="button" onClick={onClose} className={styles.cancel}>
                    Descartar
                  </button>
                  <button
                    type="button"
                    ref={continueEditingRef}
                    onClick={() => setConfirmDiscard(false)}
                    className={styles.save}
                  >
                    Continuar editando
                  </button>
                </div>
              ) : (
                <div className={styles.footerRow}>
                  <p>
                    <AvatarIcon name="verified" /> Você pode editar sempre que quiser.
                  </p>
                  <button
                    type="button"
                    className={styles.cancel}
                    onClick={requestClose}
                    disabled={isSaving}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className={styles.save}
                    onClick={() => void save()}
                    disabled={isSaving || !changed || !canSave}
                  >
                    <AvatarIcon
                      name={isSaving ? 'loading' : 'check'}
                      className={isSaving ? styles.spinner : undefined}
                    />
                    {isSaving ? 'Salvando…' : 'Salvar meu avatar'}
                  </button>
                </div>
              )}
            </footer>
          </div>
        </dialog>,
        document.body
      )}
    </>
  )
}
